import {findLastIndex} from '@benchling/js-utils';
import _ from 'underscore';

import {MAX_REACTION_CHEM_SEARCH_CLAUSES} from 'chemistry/ChemistryConstants';
// Keep in sync with MAX_REACTION_CHEM_SEARCH_CLAUSES in benchling/chemistry/constants.py
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {BatchSchema} from 'constants/SampleConstants';
import {Schema} from 'schemas/SchemaConstants';
import {validateBLASTFilter} from 'search/blast/BLASTUtil';
import validateContainerConcentrationFilters from 'search/filters/validateContainerConcentrationFilters';
import validateContainerQuantityFilters from 'search/filters/validateContainerQuantityFilters';
import validateNumericFilters from 'search/filters/validateNumericFilters';
import * as FilterUtil from 'search/FilterUtil';
import {
  Filter,
  FilterErrors,
  FilterKey,
  FILTERS,
  isChemicalStructureFilter,
  MultiValueFilter,
} from 'search/SearchConstants';
import {tagSchemaToAllOligoAwareFolderItemTypes} from 'tag-schema/util/TagSchemaUtil';
import {listToReadable} from 'util/NameUtil';

import {getFilterKeysForModelType} from '../FiltersByType';
import validateAnnotationBasesFilters from './validateAnnotationBasesFilters';
import validateFieldFilters from './validateFieldFilters';
import validateMonomerFilter, {partitionTypesForMonomerFilter} from './validateMonomerFilter';
import validateRequestFilters from './validateRequestFilters';
import validateResiduesFilters, {partitionTypesForSequenceBasesFilter} from './validateResiduesFilters';

const PARTITION_FUNCTIONS_BY_FILTER_KEY: Partial<{
  [k in FilterKey]: (filter: Filter, currentTypes: Array<string>) => [Array<string>, Array<string>];
}> = {
  [FilterKey.MONOMER]: partitionTypesForMonomerFilter,
  [FilterKey.SEQUENCE_BASES]: partitionTypesForSequenceBasesFilter,
};

export function sanitizeTypes(
  filters: Array<Filter>,
  selectedSchema: Schema | BatchSchema | null
): {filters: Array<Filter>; typeErrors: FilterErrors} {
  const typesFilter = FilterUtil.getFilterByKey(filters, FilterKey.TYPES) as MultiValueFilter;
  if (!typesFilter) {
    return {filters, typeErrors: getEmptyFilterErrors(filters)};
  }

  const oldTypes = typesFilter.value;
  let newTypes = oldTypes;

  // sanitizeTypes knocks out types that are not supported by some filter. If that filter is then removed, the
  // type remains narrowed. However, if the type is implicitly chosen by using a schema, the user now has no
  // way of selecting all types that are supported by that schema anymore. Therefore, we always seed the
  // new filters with all types supported by the selected schema. Note that this is only relevant for
  // mixed oligo / sequence schemas.
  if (selectedSchema != null && (selectedSchema as TagSchema).folderItemType != null) {
    newTypes = tagSchemaToAllOligoAwareFolderItemTypes(selectedSchema as TagSchema);
  }

  const filterKeyByInvalidatedType: {[type: string]: FilterKey} = {};
  for (const filter of filters) {
    if (filter.key === FilterKey.TYPES) {
      continue;
    }
    let [potentialNewTypes, knockedOutTypes] = _.partition(newTypes, (type) =>
      getFilterKeysForModelType(type).includes(filter.key)
    );

    const partitionFn = PARTITION_FUNCTIONS_BY_FILTER_KEY[filter.key];
    if (partitionFn) {
      const [narrowedTypes, additionalKnockedOutTypes] = partitionFn(filter, potentialNewTypes);
      potentialNewTypes = narrowedTypes;
      knockedOutTypes = knockedOutTypes.concat(additionalKnockedOutTypes);
    }

    // Only remove a filter if it leaves some types.
    if (potentialNewTypes.length) {
      newTypes = potentialNewTypes;
      for (const knockedOutType of knockedOutTypes) {
        filterKeyByInvalidatedType[knockedOutType] = filter.key;
      }
    }
  }
  const newFilters = FilterUtil.replaceByKey(filters, {...typesFilter, value: newTypes});
  return {
    filters: newFilters,
    typeErrors: newFilters.map((filter) => {
      if (newTypes.every((type) => getFilterKeysForModelType(type).includes(filter.key))) {
        return null;
      }
      const invalidatedTypes = Object.keys(filterKeyByInvalidatedType).filter((invalidatedType) =>
        getFilterKeysForModelType(invalidatedType).includes(filter.key)
      );
      if (invalidatedTypes.length === 0) {
        return null;
      }
      // eslint-disable-next-line no-restricted-syntax
      const invalidatingFilterKeys = _.uniq(invalidatedTypes.map((type) => filterKeyByInvalidatedType[type]));
      return `The filter is incompatible with the following filters: ${listToReadable(
        invalidatingFilterKeys.map((invalidatingFilterKey) => FILTERS[invalidatingFilterKey].name!)
      )}`;
    }),
  };
}

function validateTypes(filters: Array<Filter>): FilterErrors {
  const typesFilter = FilterUtil.getFilterByKey(filters, FilterKey.TYPES);
  const types = typesFilter ? (typesFilter.value as Array<string>) : [];
  return filters.map((filter) => {
    if (filter.key !== FilterKey.TYPES) {
      const invalidTypes = types.filter((type) => !getFilterKeysForModelType(type).includes(filter.key));
      if (invalidTypes.length > 0) {
        if (invalidTypes.length === types.length) {
          return `The filter is not supported for ${types.length === 1 ? 'the' : 'any'} selected type.`;
        } else {
          return 'The filter is not supported for some selected types.';
        }
      }
    }
    return null;
  });
}

function validateFilterDependencies(filters: Array<Filter>): FilterErrors {
  return filters.map((filter) => {
    if (FILTERS[filter.key].dependencies && FILTERS[filter.key].dependencies!.length > 0) {
      const missingDependencies = FILTERS[filter.key].dependencies!.filter(
        (dependency) => !filters.some((f) => f.key === dependency)
      );
      if (missingDependencies.length > 0) {
        return `The filter requires the following filter(s) to be present: ${listToReadable(
          missingDependencies.map((dependency) => FILTERS[dependency].name!)
        )}.`;
      }
    }

    return null;
  });
}

function isReactionOnlySearch(filters: Array<Filter>): boolean {
  const typesFilter = FilterUtil.getFilterByKey(filters, FilterKey.TYPES) as MultiValueFilter | null;
  return typesFilter?.value.length === 1 && typesFilter.value[0] === ModelTypeConstants.REACTION;
}

function validateChemicalStructureFilterCount(filters: Array<Filter>): FilterErrors {
  if (!isReactionOnlySearch(filters)) {
    return filters.map(() => null);
  }
  let chemicalStructureFilterCount = 0;
  return filters.map((filter) => {
    if (!isChemicalStructureFilter(filter)) {
      return null;
    }
    chemicalStructureFilterCount += 1;
    if (chemicalStructureFilterCount <= MAX_REACTION_CHEM_SEARCH_CLAUSES) {
      return null;
    }
    return `Chemical structure search supports up to ${MAX_REACTION_CHEM_SEARCH_CLAUSES} structures.`;
  });
}

/**
 * @returns Array where the ith element is an error message for the ith filter, if invalid, else null
 */
export function validateFilters(
  filters: Array<Filter>,
  selectedSchema: Schema | BatchSchema | null
): FilterErrors {
  const errorsLists = [
    validateTypes(filters),
    validateFieldFilters(filters, selectedSchema),
    validateRequestFilters(filters, selectedSchema),
    validateContainerQuantityFilters(filters),
    validateContainerConcentrationFilters(filters),
    validateNumericFilters(filters),
    validateResiduesFilters(filters),
    validateAnnotationBasesFilters(filters),
    validateMonomerFilter(filters),
    validateBLASTFilter(filters),
    validateChemicalStructureFilterCount(filters),
    validateFilterDependencies(filters),
  ];
  return reduceErrors(...errorsLists);
}

export function reduceErrors(...errorsLists: Array<FilterErrors>): FilterErrors {
  // Note that the order we zip is the order of priority of the error message for a given filter.
  return _.zip(...errorsLists).map((errors: FilterErrors) => errors.find((error) => error != null) || null);
}

export function getEmptyFilterErrors(filters: Array<Filter>): FilterErrors {
  return filters.map(() => null);
}

/**
 * Convenience function to find the filter error for a given filter key that is guaranteed to be unique.
 *
 * Should not be used for filters that can be applied multiple times.
 *
 * @param filters List of filters
 * @param filterErrors Filter errors for specified filters
 * @param filterKey Key of filter to get errors for. Must be unique in filters.
 * @returns The error string for the specified filter key, or null if it is valid.
 */
export function getFilterErrorForFilter(
  filters: Array<Filter>,
  filterErrors: FilterErrors,
  filterKey: FilterKey
): string | null {
  const index = filters.findIndex((filter) => filter.key === filterKey);

  if (index === -1) {
    throw new Error(`${filterKey} not found in filters.`);
  }
  if (findLastIndex(filters, (filter) => filter.key === filterKey) !== index) {
    throw new Error(`${filterKey} was found multiple times in filters.`);
  }

  return filterErrors[index];
}

/**
 * Convenience function to find the first error across filters with a key that can appear multiple times.
 */
export function getFirstFilterErrorForFilter(
  filters: Array<Filter>,
  filterErrors: FilterErrors,
  filterKey: FilterKey
): string | null {
  const matchingFilterErrors = filters.flatMap((filter, index) =>
    filter.key === filterKey ? [filterErrors[index]] : []
  );
  if (matchingFilterErrors.length === 0) {
    throw new Error(`${filterKey} not found in filters.`);
  }
  return matchingFilterErrors.find((error) => error != null) ?? null;
}
